import { logger } from '@/shared/utils';
import { jwtDecode } from 'jwt-decode';

const STORAGE_KEY = 'mirrorfly_data';
const JWT_HEADER = 'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0'; // Base64 for {"alg":"none","typ":"JWT"}

class Storage {
   constructor() {
      this.data = {};
      this.initialized = false;
      this.listeners = new Set();

      if (typeof window !== 'undefined') {
         window.addEventListener('storage', event => {
            if (event.key === STORAGE_KEY) {
               try {
                  const decoded = event.newValue ? jwtDecode(event.newValue) : {};
                  this.data = decoded;
                  this.notifyListeners();
               } catch (error) {
                  logger.error('Error syncing consolidated storage from event:', error);
               }
            }
         });
      }
   }

   initialize() {
      if (this.initialized) return;
      try {
         const stored = localStorage.getItem(STORAGE_KEY);
         if (stored) {
            // Use jwt-decode to read the payload
            const decoded = jwtDecode(stored);
            this.data = decoded || {};
         }
      } catch (error) {
         logger.error('Error initializing consolidated storage:', error);
         this.data = {};
      }
      this.initialized = true;
   }

   save() {
      try {
         const stringified = JSON.stringify(this.data);
         // Encode to Base64 and format as JWT (header.payload.signature)
         // Using TextEncoder to handle UTF-8 without using deprecated unescape
         const uint8Array = new TextEncoder().encode(stringified);
         const binString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');
         const base64Payload = window.btoa(binString).replace(/=/g, '');
         const jwtLike = `${JWT_HEADER}.${base64Payload}.`;
         localStorage.setItem(STORAGE_KEY, jwtLike);
      } catch (error) {
         logger.error('Error saving consolidated storage:', error);
      }
   }

   setItem(key, value) {
      if (!this.initialized) this.initialize();
      this.data[key] = value;
      this.save();
   }

   getItem(key) {
      if (!this.initialized) this.initialize();
      return this.data[key] || null;
   }

   removeItem(key) {
      if (!this.initialized) this.initialize();
      delete this.data[key];
      this.save();
   }

   clear() {
      this.data = {};
      localStorage.removeItem(STORAGE_KEY);
   }

   // Optional: add listener support if needed for components to react to storage changes
   subscribe(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
   }

   notifyListeners() {
      this.listeners.forEach(listener => listener(this.data));
   }
}

const storage = new Storage();
export default storage;
